(() => {
  if (customElements.get('shoe-size-guide')) return;

  class ShoeSizeGuide extends HTMLElement {
    constructor() {
      super();
      this.onClose = this.finishClose.bind(this);
      this.onCancel = (event) => {
        event.preventDefault();
        event.stopPropagation();
        // Dawn's underlying quick-add dialog also listens for Escape keyup.
        // Consume that release even if native close has already restored focus.
        document.addEventListener('keyup', (release) => {
          if (release.key === 'Escape') release.stopPropagation();
        }, { capture: true, once: true });
        this.close();
      };
      this.onKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-shoe-size-guide-close]')) this.close();
        else if (event.target.closest('[data-shoe-size-guide-apply]')) this.applySize();
        else if (event.target.closest('[data-shoe-size-row]')) this.selectRow(event.target.closest('[data-shoe-size-row]'));
        else if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
      });
    }

    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;
      this.dialog.addEventListener('close', this.onClose);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      if (!this.dialog) return;
      this.dialog.removeEventListener('close', this.onClose);
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('keydown', this.onKeydown);
      this.restoreFocus = false;
      if (this.dialog.open) this.dialog.close();
      this.finishClose();
    }

    static normalizeSize(value) {
      const size = String(value ?? '').trim()
        .replace(/^(?:(?:us|eu|uk|size)\s*)+/i, '')
        .replace(/(?:\s*(?:us|eu|uk))+$/i, '')
        .trim().replace(',', '.');
      return /^\d+(?:\.\d+)?$/.test(size) ? String(Number(size)) : null;
    }

    get picker() {
      return document.getElementById(this.dataset.pickerId);
    }

    get optionGroup() {
      return this.picker?.querySelector('[data-shoe-size-option]');
    }

    get rows() {
      return [...this.querySelectorAll('[data-shoe-size-row]')];
    }

    choiceFor(row) {
      const size = ShoeSizeGuide.normalizeSize(row.dataset[this.dataset.sizeSystem]);
      if (size === null) return null;
      const choices = this.optionGroup?.querySelectorAll('select option, input[type="radio"]') || [];
      const choice = [...choices].find((input) => ShoeSizeGuide.normalizeSize(input.value) === size);
      if (!choice) return null;
      return {
        choice,
        control: choice.tagName === 'OPTION' ? choice.closest('select') : choice,
        available: !choice.disabled && !choice.classList.contains('disabled') && choice.dataset.optionAvailable !== 'false',
      };
    }

    show(opener) {
      if (!this.dialog || this.dialog.open || typeof this.dialog.showModal !== 'function') return;
      this.opener = opener;
      this.focusTarget = null;
      this.restoreFocus = true;
      const group = this.optionGroup;
      const current = group?.querySelector('select')?.value || group?.querySelector('input:checked')?.value;
      const size = ShoeSizeGuide.normalizeSize(current);
      this.rows.forEach((row) => row.classList.toggle('is-unavailable', !this.choiceFor(row)?.available));
      const selected = size === null ? null : this.rows.find((row) => ShoeSizeGuide.normalizeSize(row.dataset[this.dataset.sizeSystem]) === size);
      this.selectRow(selected);
      this.dialog.showModal();
      document.body.classList.add('shoe-size-guide-open');
      opener.setAttribute('aria-expanded', 'true');
      selected?.scrollIntoView({ block: 'nearest' });
    }

    selectRow(row) {
      this.selectedRow = row;
      this.rows.forEach((item) => {
        item.classList.toggle('is-selected', item === row);
        item.querySelector('button').setAttribute('aria-pressed', String(item === row));
      });
      for (const summary of this.querySelectorAll('[data-shoe-size-summary]')) {
        summary.textContent = row?.dataset[summary.dataset.shoeSizeSummary] || '—';
      }
      const primary = this.querySelector('[data-shoe-size-primary]');
      if (primary) primary.textContent = row?.dataset[this.dataset.sizeSystem] || '—';
      const apply = this.querySelector('[data-shoe-size-guide-apply]');
      if (apply) {
        const available = Boolean(row && this.choiceFor(row)?.available);
        apply.disabled = !available;
        this.querySelector('[data-shoe-size-guide-apply-label]').textContent = row && !available ? this.dataset.unavailableLabel : this.dataset.applyLabel;
      }
    }

    applySize() {
      if (!this.selectedRow) return;
      const match = this.choiceFor(this.selectedRow);
      if (!match?.available) {
        this.selectRow(this.selectedRow);
        return;
      }
      const { choice, control } = match;
      const changed = choice.tagName === 'OPTION' ? control.value !== choice.value : !choice.checked;
      if (choice.tagName === 'OPTION') control.value = choice.value;
      else choice.checked = true;
      this.focusTarget = control;
      this.close();
      // Use Dawn's existing change flow to retain the other options, inventory,
      // product form IDs, URL updates, and section-rendered prices.
      if (changed) control.dispatchEvent(new Event('change', { bubbles: true }));
    }

    close() {
      if (this.dialog?.open) this.dialog.close();
    }

    finishClose() {
      if (!document.querySelector('shoe-size-guide dialog[open]')) document.body.classList.remove('shoe-size-guide-open');
      const opener = [...document.querySelectorAll('[data-shoe-size-guide-open]')]
        .find((button) => button.dataset.shoeSizeGuideOpen === this.dialog?.id);
      opener?.setAttribute('aria-expanded', 'false');
      if (!this.isConnected || !this.restoreFocus) return;
      const target = (this.focusTarget?.isConnected ? this.focusTarget : document.getElementById(this.focusTarget?.id)) || opener;
      if (!target) return;
      const parentModal = this.closest('quick-add-modal[open], modal-dialog[open]');
      if (parentModal && typeof trapFocus === 'function') trapFocus(parentModal, target);
      else target.focus({ preventScroll: true });
    }

    handleKeydown(event) {
      // Native dialog focus management must take precedence over any underlying
      // Dawn quick-add modal's document-level Tab handler.
      if (event.key === 'Tab') event.stopPropagation();
      if (event.key === 'Escape') event.stopPropagation();
      const button = event.target.closest('.shoe-size-guide__row-button');
      if (!button || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      const rows = this.rows;
      const index = rows.indexOf(button.closest('[data-shoe-size-row]'));
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? rows.length - 1 : Math.max(0, Math.min(rows.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
      this.selectRow(rows[next]);
      rows[next]?.querySelector('button').focus();
    }
  }

  customElements.define('shoe-size-guide', ShoeSizeGuide);
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-shoe-size-guide-open]');
    if (!opener) return;
    const guide = document.getElementById(opener.dataset.shoeSizeGuideOpen)?.closest('shoe-size-guide');
    if (!guide) return;
    event.preventDefault();
    guide.show(opener);
  });
})();
